import type {
  BenefitDisposition,
  CommitmentType,
  PromiseKind,
} from './ma-deal-case';
import type { CustomerOverlapComponentKind } from './ma-synergy-customer-overlap';
import {
  MA_SYNERGY_COMMITMENT_V1_SCHEMA_VERSION,
  MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION,
  isMaSynergyCommitmentSchemaVersion,
  validateSynergyLeverCommitment,
  type SynergyLeverCommitment,
} from './ma-synergy-commitment';

export const MA_DEAL_MODELED_UNDERWRITING_OVERLAY_KIND =
  'modeled_underwriting_overlay' as const;

export type MaDealSourceClassification = Readonly<{
  commitment: SynergyLeverCommitment;
  cohort: PromiseKind;
  coherentPortfolio: boolean;
  commitmentType: CommitmentType;
  benefitDisposition: BenefitDisposition;
}>;

export function classifyMaDealSourceCommitment(
  source: unknown,
): MaDealSourceClassification {
  const schemaVersion = sourceSchemaVersion(source);
  if (!isMaSynergyCommitmentSchemaVersion(schemaVersion)) {
    throw new Error(
      `unsupported_ma_synergy_commitment_schema:${String(schemaVersion)}`,
    );
  }

  const validation = validateSynergyLeverCommitment(source);
  if (!validation.ok) {
    const code =
      schemaVersion === MA_SYNERGY_COMMITMENT_V2_SCHEMA_VERSION
        ? 'invalid_ma_synergy_commitment_v2'
        : 'invalid_ma_synergy_commitment_v1';
    throw new Error(`${code}:${validation.errors.join('; ')}`);
  }

  if (schemaVersion === MA_SYNERGY_COMMITMENT_V1_SCHEMA_VERSION) {
    return classifyLegacyUnderwriting(validation.value);
  }

  return classifyCustomerOverlap(validation.value);
}

function classifyLegacyUnderwriting(
  commitment: SynergyLeverCommitment,
): MaDealSourceClassification {
  return {
    commitment,
    cohort: 'board_approved',
    coherentPortfolio: true,
    commitmentType: 'synergy_benefit',
    benefitDisposition: 'benefit',
  };
}

function classifyCustomerOverlap(
  commitment: SynergyLeverCommitment,
): MaDealSourceClassification {
  const component = commitment.customerOverlap?.component;
  if (
    commitment.certaintyClass !== 'c2_modelled_upside' ||
    component === undefined
  ) {
    throw new Error(
      'invalid_ma_synergy_commitment_v2:typed c2 customerOverlap extension required',
    );
  }
  return {
    commitment,
    cohort: MA_DEAL_MODELED_UNDERWRITING_OVERLAY_KIND,
    coherentPortfolio: false,
    ...customerOverlapSemantics(component),
  };
}

function customerOverlapSemantics(
  component: CustomerOverlapComponentKind,
): Pick<MaDealSourceClassification, 'commitmentType' | 'benefitDisposition'> {
  if (component === 'pricing') {
    return {
      commitmentType: 'dis_synergy_or_leakage',
      benefitDisposition: 'dis_synergy',
    };
  }
  if (component === 'retention_risk') {
    return {
      commitmentType: 'retention_or_asset_protection',
      benefitDisposition: 'dis_synergy',
    };
  }
  return {
    commitmentType: 'synergy_benefit',
    benefitDisposition: 'benefit',
  };
}

function sourceSchemaVersion(source: unknown): unknown {
  return isRecord(source) ? source.schemaVersion : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
